import { config } from '@lingoda/config';
import {
    devCheckVersionOnIdleTimeInSeconds,
    devMockedLocalReleaseId,
} from '@lingoda/local-feature-flags';
import { captureError } from '@lingoda/monitor';
import { ContextError, type ErrorContexts } from '@lingoda/errors';
import { wait } from '@lingoda/utils';

const mockedLocalReleaseId = devMockedLocalReleaseId();

if (mockedLocalReleaseId) {
    // @ts-expect-error - we're mocking the releaseId for local development overwriting readonly config property
    config.releaseId = mockedLocalReleaseId;
}

const TWO_HOURS_SECONDS = 2 * 60 * 60;
const DEV_TESTING_IDLE_TIME_IN_SECONDS = devCheckVersionOnIdleTimeInSeconds();
const IDLE_TIME_MS = (DEV_TESTING_IDLE_TIME_IN_SECONDS ?? TWO_HOURS_SECONDS) * 1000;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5_000;
const FETCH_TIMEOUT_MS = 10_000;

const validateReleaseId = (releaseId: string | undefined) => {
    if (!releaseId) {
        return false;
    }

    // if mocking locally, ignore checking server provided 'none' value
    if (mockedLocalReleaseId) {
        return true;
    }

    // can be release-<number>
    return /^release-\d+$/.test(releaseId);
};

const isOnline = () => {
    return navigator.onLine;
};

// Store the current fetch controller so we can abort on navigation
let currentFetchController: AbortController | undefined;

const fetchReleaseId = async (retryCount = 0): Promise<string | undefined> => {
    // Skip if offline - don't spam Sentry with network errors
    if (!isOnline()) {
        return undefined;
    }

    // Abort any previous request before starting a new one
    if (currentFetchController) {
        currentFetchController.abort();
    }

    const controller = new AbortController();
    currentFetchController = controller;
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
        // Add a cache-busting query param to ensure we always get the latest version from the server
        const cacheBuster = `cb=${Date.now()}`;
        const requestUrl = `/systemConfig.js?${cacheBuster}`;

        const response = await fetch(requestUrl, {
            signal: controller.signal,
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
            },
        });

        clearTimeout(timeoutId);
        currentFetchController = undefined;

        if (!response.ok) {
            if (response.status >= 500 && retryCount < MAX_RETRIES) {
                await wait(RETRY_DELAY_MS);

                return fetchReleaseId(retryCount + 1);
            }

            // Only report to Sentry after all retries are exhausted
            if (retryCount >= MAX_RETRIES || response.status < 500) {
                captureError(
                    new FetchSystemConfigError({
                        data: {
                            status: response.status,
                            statusText: response.statusText,
                            retryCount,
                        },
                    }),
                );
            }

            return undefined;
        }

        const text = await response.text();
        const match = text.match(/["']releaseId["']\s*:\s*["']([^"']+)["']/);
        const releaseId = match ? match[1] : undefined;

        return releaseId;
    } catch (error) {
        clearTimeout(timeoutId);
        currentFetchController = undefined;

        // Check if it's an abort error (from timeout or navigation)
        const isAbortError = error instanceof Error && error.name === 'AbortError';

        // Don't report or retry abort errors - they're intentional
        if (isAbortError) {
            return undefined;
        }

        // Check if it's a network error (likely offline/timeout)
        const isNetworkError =
            error instanceof TypeError ||
            (error instanceof Error && error.message.includes('fetch'));

        // Retry on network errors
        if (isNetworkError && retryCount < MAX_RETRIES && isOnline()) {
            await wait(RETRY_DELAY_MS);

            return fetchReleaseId(retryCount + 1);
        }

        // Only report to Sentry if:
        // 1. We've exhausted all retries
        // 2. The user appears to be online
        // 3. It's not a network/abort error
        if (retryCount >= MAX_RETRIES && isOnline()) {
            const err = {
                errorMessage: error instanceof Error ? error.message : String(error),
                errorName: error instanceof Error ? error.name : 'Unknown',
                retryCount,
                isOnline: isOnline(),
            };
            captureError(
                new FetchSystemConfigError({
                    data: err,
                }),
            );
        }

        return undefined;
    }
};

let idleCheckIntervalId: number | undefined;

const startIdleTimer = () => {
    idleCheckIntervalId = window.setInterval(async () => {
        // Skip version check if offline to prevent unnecessary errors
        if (!isOnline()) {
            return;
        }

        const localReleaseId = config.releaseId;
        const serverReleaseId = await fetchReleaseId();
        const isServerReleaseIdValid = validateReleaseId(serverReleaseId);

        if (isServerReleaseIdValid && serverReleaseId !== localReleaseId) {
            window.location.reload();
        }
    }, IDLE_TIME_MS);
};
const clearIdleTimer = () => {
    if (idleCheckIntervalId !== undefined) {
        clearInterval(idleCheckIntervalId);
        idleCheckIntervalId = undefined;
    }
};

const cleanup = () => {
    clearIdleTimer();
    // Abort any pending fetch request
    if (currentFetchController) {
        currentFetchController.abort();
        currentFetchController = undefined;
    }
};

const restartIdleTimer = () => {
    clearIdleTimer();
    startIdleTimer();
};

const handleHistoryChange = () => {
    // Abort any pending request before restarting timer
    if (currentFetchController) {
        currentFetchController.abort();
        currentFetchController = undefined;
    }
    restartIdleTimer();
};

const handleLoad = (history: History) => {
    startIdleTimer();

    // restart idle timer on navigation page change to prevent surprise reloads
    const unsubscribe = history.listen(handleHistoryChange);

    // Cleanup on page unload to prevent errors from aborted requests
    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('pagehide', cleanup);

    return () => {
        cleanup();
        unsubscribe?.();
        window.removeEventListener('beforeunload', cleanup);
        window.removeEventListener('pagehide', cleanup);
    };
};

/**
 * Initializes a mechanism to reload the application if a new version (releaseId) is available comparing to already loaded lgConfig.releaseId
 * Runs only if the user has been idle (did not navigate nor reload page) for a 2h time period.
 */
export const initForceReloadOutdatedVersionOnIdle = ({ history }: { history: History }) => {
    const eventHandler = () => handleLoad(history);

    window.addEventListener('load', eventHandler);
};

type History = {
    listen: (listener: () => void) => (() => void) | void;
};

class FetchSystemConfigError extends ContextError {
    constructor(context: ErrorContexts) {
        super('Failed to fetch systemConfig.js', { context });
        this.name = 'FetchSystemConfigError';
    }
}
